import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  ExternalLink,
  Images,
  Inbox,
  LockKeyhole,
  LoaderCircle,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  adminMediaApi,
  loadEventGallery,
  loadEventPhotoDelivery,
  loadEventPhotographerApplications,
  reviewEventPhotographerApplication,
  loadStudioMembers,
} from "./api";
import BulkMediaUploader from "./BulkMediaUploader";

const EVENT_DRAFT_KEY = "photox.admin.eventDraft.v1";

const emptyForm = () => ({
  studioId: "",
  eventTypeId: "",
  name: "",
  clientEmail: "",
  clientDisplayName: "",
  photographerIds: [],
  assignmentReason: "",
  startsAt: "",
  endsAt: "",
  timezone: "Asia/Kolkata",
  venueName: "",
  venueAddress: "",
  venueCity: "",
  venueCountry: "India",
  planCode: "",
  retentionDays: "90",
  accessStartsAt: "",
  accessExpiresAt: "",
  guestUploadsEnabled: true,
  billingRequired: true,
});

function localDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDate(value) {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "0 GB";
  return `${Math.round(bytes / 1073741824)} GB`;
}

function formatFileSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "Size unavailable";
  if (bytes < 1048576) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1048576).toFixed(bytes >= 10485760 ? 0 : 1)} MB`;
}

function Section({ title, text, children }) {
  return (
    <fieldset>
      <legend>{title}</legend>
      <p>{text}</p>
      {children}
    </fieldset>
  );
}

function GalleryMediaPlaceholder({ status, failure }) {
  if (["uploading", "processing"].includes(status)) {
    return (
      <div className="event-gallery-media-placeholder is-processing">
        <LoaderCircle className="spin" size={25} />
        <span>Processing image…</span>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="event-gallery-media-placeholder is-failed">
        <AlertTriangle size={25} />
        <span>{failure?.message || "Image processing failed"}</span>
      </div>
    );
  }
  return (
    <div className="event-gallery-media-placeholder">
      <Images size={25} />
      <span>{status || "Preparing image…"}</span>
    </div>
  );
}

export function EventManagement({
  events = [],
  inquiries = [],
  studios = [],
  subscriptions,
  eventTypes = [],
  photographers = [],
  plans = [],
  token,
  create = async () => false,
  update = async () => false,
  transition = async () => false,
  remove = async () => false,
  publish = async () => false,
  sponsorPlan = async () => false,
  showQr = () => undefined,
  setToast = () => undefined,
  reviewRequests = () => undefined,
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stage, setStage] = useState("details");
  const [members, setMembers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [galleryTarget, setGalleryTarget] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [galleryKind, setGalleryKind] = useState("all");
  const [galleryPrivacy, setGalleryPrivacy] = useState("all");
  const [galleryQuery, setGalleryQuery] = useState("");
  const [inspectedPhoto, setInspectedPhoto] = useState(null);
  const [originalBusy, setOriginalBusy] = useState(false);
  const [deletingPhotoIds, setDeletingPhotoIds] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(() => new Set());
  const [form, setForm] = useState(emptyForm);

  const paidEventIds = new Set(
    (subscriptions?.subscriptions || [])
      .filter(
        (item) =>
          ["active", "trialing"].includes(item.status) && item.event?.id,
      )
      .map((item) => item.event.id),
  );
  const approvedIds = new Set(
    photographers
      .filter(
        (profile) =>
          profile.status === "approved" && profile.user?.status === "active",
      )
      .map((profile) => profile.user.id),
  );
  const approvedDirectory = photographers
    .filter(
      (profile) =>
        profile.status === "approved" && profile.user?.status === "active",
    )
    .map((profile) => ({
      id: profile.id,
      role: "photographer",
      status: "active",
      user: profile.user,
    }));
  const availablePhotographers = (form.studioId ? members : approvedDirectory).filter(
    (member) =>
      member.status === "active" &&
      ["owner", "photographer"].includes(member.role) &&
      approvedIds.has(member.user?.id),
  );
  const visibleEvents = events.filter((item) => {
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    const needle = query.trim().toLowerCase();
    const text = [
      item.name,
      item.client?.displayName,
      item.client?.email,
      item.eventType?.name,
      item.studio?.name,
      ...(item.assignedPhotographers || []).map(
        (photographer) =>
          `${photographer.displayName || ""} ${photographer.email || ""}`,
      ),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return matchesStatus && (!needle || text.includes(needle));
  });
  const visibleRequests = inquiries.filter((item) => {
    if (["converted", "declined", "cancelled"].includes(item.status))
      return false;
    if (!["all", "requests"].includes(statusFilter)) return false;
    const needle = query.trim().toLowerCase();
    const text = [
      item.reference,
      item.eventName,
      item.eventType?.name,
      item.clientName,
      item.email,
      item.venue?.city,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return !needle || text.includes(needle);
  });
  const assignmentTeamChanged = Boolean(
    editing &&
    (editing.assignedPhotographers || [])
      .map((photographer) => photographer.id)
      .sort()
      .join(",") !== [...form.photographerIds].sort().join(","),
  );
  const galleryCounts = galleryPhotos.reduce(
    (counts, photo) => ({
      total: counts.total + 1,
      private: counts.private + (photo.privacy === "private" ? 1 : 0),
      shared: counts.shared + (photo.privacy === "shared" ? 1 : 0),
      ready: counts.ready + (photo.status === "ready" ? 1 : 0),
    }),
    { total: 0, private: 0, shared: 0, ready: 0 },
  );
  const visibleGalleryPhotos = galleryPhotos.filter((photo) => {
    const needle = galleryQuery.trim().toLowerCase();
    const searchable = [
      photo.originalFilename,
      photo.caption,
      photo.uploader?.displayName,
      photo.kind,
      photo.privacy,
      photo.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      (galleryKind === "all" || photo.kind === galleryKind) &&
      (galleryPrivacy === "all" || photo.privacy === galleryPrivacy) &&
      (!needle || searchable.includes(needle))
    );
  });
  const galleryHasPendingMedia = galleryPhotos.some((photo) =>
    ["uploading", "processing"].includes(photo.status),
  );
  const selectedGalleryPhotos = galleryPhotos.filter((photo) =>
    selectedPhotoIds.has(photo.id),
  );
  const allVisibleGalleryPhotosSelected =
    visibleGalleryPhotos.length > 0 &&
    visibleGalleryPhotos.every((photo) => selectedPhotoIds.has(photo.id));

  useEffect(() => {
    if (!galleryTarget || !galleryHasPendingMedia) return undefined;
    let cancelled = false;
    const refreshProcessingMedia = async () => {
      try {
        const refreshed = await loadEventGallery(token, galleryTarget.id);
        if (cancelled) return;
        setGalleryPhotos(refreshed);
        setInspectedPhoto((current) =>
          current
            ? refreshed.find((photo) => photo.id === current.id) || null
            : null,
        );
      } catch {
        // The manual Refresh control remains available if an intermittent request fails.
      }
    };
    const interval = window.setInterval(refreshProcessingMedia, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [galleryHasPendingMedia, galleryTarget, token]);

  useEffect(() => {
    if (!open || editing || typeof window === "undefined") return;
    window.localStorage.setItem(EVENT_DRAFT_KEY, JSON.stringify(form));
  }, [editing, form, open]);

  async function loadMembers(studioId) {
    if (!studioId) {
      setMembers([]);
      return;
    }
    try {
      setMembers(await loadStudioMembers(token, studioId));
    } catch (requestError) {
      setToast(requestError.message || "Unable to load studio members");
    }
  }

  async function openGallery(item) {
    setGalleryTarget(item);
    setGalleryPhotos([]);
    setGalleryError("");
    setGalleryKind("all");
    setGalleryPrivacy("all");
    setGalleryQuery("");
    setInspectedPhoto(null);
    setSelectedPhotoIds(new Set());
    setDeletingPhotoIds([]);
    setGalleryBusy(true);
    try {
      setGalleryPhotos(await loadEventGallery(token, item.id));
    } catch (requestError) {
      setGalleryError(
        requestError.message || "Unable to load this event gallery",
      );
    } finally {
      setGalleryBusy(false);
    }
  }

  async function openOriginal(photo) {
    if (!galleryTarget || photo.status !== "ready") return;
    // Open the tab synchronously from the click, before the signed-URL request.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setOriginalBusy(true);
    try {
      const delivery = await loadEventPhotoDelivery(
        token,
        galleryTarget.id,
        photo.id,
        "original",
      );
      if (tab && !tab.closed) tab.location.replace(delivery.url);
      else window.open(delivery.url, "_blank", "noopener,noreferrer");
    } catch (requestError) {
      if (tab && !tab.closed) tab.close();
      setToast(requestError.message || "Unable to open the original image");
    } finally {
      setOriginalBusy(false);
    }
  }

  function toggleGalleryPhotoSelection(photoId) {
    setSelectedPhotoIds((current) => {
      const next = new Set(current);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }

  function toggleVisibleGalleryPhotoSelection() {
    setSelectedPhotoIds((current) => {
      const next = new Set(current);
      if (allVisibleGalleryPhotosSelected) {
        visibleGalleryPhotos.forEach((photo) => next.delete(photo.id));
      } else {
        visibleGalleryPhotos.forEach((photo) => next.add(photo.id));
      }
      return next;
    });
  }

  async function deleteGalleryPhotos(photos) {
    const targets = photos.filter(Boolean);
    if (!galleryTarget || deletingPhotoIds.length || !targets.length) return;
    const count = targets.length;
    const description =
      count === 1
        ? targets[0].originalFilename || "this image"
        : `${count} selected images`;
    if (
      !window.confirm(
        `Permanently delete ${description}? This also removes the stored originals and derivatives.`,
      )
    ) {
      return;
    }
    const targetIds = new Set(targets.map((photo) => photo.id));
    setDeletingPhotoIds([...targetIds]);
    setGalleryError("");
    setGalleryPhotos((current) =>
      current.filter((photo) => !targetIds.has(photo.id)),
    );
    setSelectedPhotoIds((current) => {
      const next = new Set(current);
      targetIds.forEach((photoId) => next.delete(photoId));
      return next;
    });
    setInspectedPhoto((current) =>
      targetIds.has(current?.id) ? null : current,
    );
    setToast(count === 1 ? "Deleting image…" : `Deleting ${count} images…`);
    const failures = [];
    let cursor = 0;
    try {
      await Promise.all(
        Array.from({ length: Math.min(4, targets.length) }, async () => {
          while (cursor < targets.length) {
            const photo = targets[cursor];
            cursor += 1;
            try {
              await adminMediaApi(token).remove(galleryTarget.id, photo.id);
            } catch (error) {
              failures.push({ photo, error });
            }
          }
        }),
      );
      if (failures.length) {
        setGalleryError(
          `${failures.length} image${failures.length === 1 ? "" : "s"} could not be deleted. The gallery has been refreshed.`,
        );
        setGalleryPhotos(await loadEventGallery(token, galleryTarget.id));
      } else {
        setToast(count === 1 ? "Image deleted" : `${count} images deleted`);
      }
    } catch (requestError) {
      setGalleryError(
        requestError.message || "Unable to refresh the image gallery",
      );
    } finally {
      setDeletingPhotoIds([]);
    }
  }

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function chooseStudio(studioId) {
    setForm((current) => ({
      ...current,
      studioId,
      photographerIds: [],
    }));
    await loadMembers(studioId);
  }

  function startCreate() {
    let saved = null;
    if (typeof window !== "undefined") {
      try {
        saved = JSON.parse(window.localStorage.getItem(EVENT_DRAFT_KEY));
      } catch {
        saved = null;
      }
    }
    const next = { ...emptyForm(), ...(saved || {}) };
    if (!next.planCode && plans[0]) next.planCode = plans[0].code;
    setForm(next);
    setEditing(null);
    setApplications([]);
    setStage("details");
    setOpen(true);
    loadMembers(next.studioId);
  }

  function startEdit(item) {
    const next = {
      studioId: item.studio?.id || "",
      eventTypeId: item.eventType?.id || "",
      name: item.name || "",
      clientEmail: item.client?.email || "",
      clientDisplayName: item.client?.displayName || "",
      photographerIds: (item.assignedPhotographers || []).map(
        (photographer) => photographer.id,
      ),
      assignmentReason: "",
      startsAt: localDateTime(item.startsAt),
      endsAt: localDateTime(item.endsAt),
      timezone: item.timezone || "Asia/Kolkata",
      venueName: item.venue?.name || "",
      venueAddress: item.venue?.address || "",
      venueCity: item.venue?.city || "",
      venueCountry: item.venue?.country || "India",
      planCode: item.requestedPlanCode || plans[0]?.code || "",
      retentionDays: String(item.retentionDays || 90),
      accessStartsAt: localDateTime(item.accessStartsAt),
      accessExpiresAt: localDateTime(item.accessExpiresAt),
      guestUploadsEnabled: item.guestUploadsEnabled !== false,
      billingRequired: item.billingRequired ?? true,
    };
    setForm(next);
    setEditing(item);
    setStage("details");
    setOpen(true);
    loadMembers(next.studioId);
    loadEventPhotographerApplications(token, item.id)
      .then(setApplications)
      .catch((requestError) =>
        setToast(requestError.message || "Unable to load photographer registrations"),
      );
  }

  async function reviewApplication(application, status) {
    setBusy(true);
    try {
      const updated = await reviewEventPhotographerApplication(
        token,
        editing.id,
        application.id,
        status,
        status === "approved"
          ? "Approved for this paid event"
          : "Registration not selected for this event",
      );
      setApplications((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      if (status === "approved" && application.photographer?.id) {
        setForm((current) => ({
          ...current,
          photographerIds: [
            ...new Set([...current.photographerIds, application.photographer.id]),
          ],
        }));
        setEditing((current) => ({
          ...current,
          assignedPhotographers: [
            ...(current.assignedPhotographers || []),
            application.photographer,
          ].filter(
            (photographer, index, items) =>
              items.findIndex((item) => item.id === photographer.id) === index,
          ),
        }));
      }
      setToast(
        status === "approved"
          ? "Photographer registration approved and assigned"
          : "Photographer registration rejected",
      );
    } catch (requestError) {
      setToast(requestError.message || "Unable to review registration");
    } finally {
      setBusy(false);
    }
  }

  function togglePhotographer(id) {
    setForm((current) => ({
      ...current,
      photographerIds: current.photographerIds.includes(id)
        ? current.photographerIds.filter((value) => value !== id)
        : [...current.photographerIds, id],
    }));
  }

  function detailsPayload() {
    const values = {
      name: form.name.trim(),
      startsAt: new Date(form.startsAt).toISOString(),
      ...(form.endsAt ? { endsAt: new Date(form.endsAt).toISOString() } : {}),
      timezone: form.timezone,
      venue: {
        name: form.venueName.trim(),
        address: form.venueAddress.trim(),
        city: form.venueCity.trim(),
        country: form.venueCountry.trim(),
      },
      retentionDays: Number(form.retentionDays),
      guestUploadsEnabled: form.guestUploadsEnabled,
      ...(form.accessStartsAt
        ? { accessStartsAt: new Date(form.accessStartsAt).toISOString() }
        : {}),
      ...(form.accessExpiresAt
        ? { accessExpiresAt: new Date(form.accessExpiresAt).toISOString() }
        : {}),
    };
    if (editing && form.billingRequired !== undefined) {
      values.billingRequired = form.billingRequired;
    }
    if (!editing || form.eventTypeId !== editing.eventType?.id) {
      values.eventTypeId = form.eventTypeId;
    }
    if (!editing || form.planCode !== editing.requestedPlanCode) {
      values.planCode = form.planCode;
    }
    return values;
  }

  async function submit(event) {
    event.preventDefault();
    if (stage === "details") {
      setStage("review");
      return;
    }
    setBusy(true);
    let succeeded;
    if (editing) {
      succeeded = await update(editing, detailsPayload(), {
        clientEmail: form.clientEmail.trim(),
        ...(form.clientDisplayName.trim()
          ? { clientDisplayName: form.clientDisplayName.trim() }
          : {}),
        assignedPhotographerIds: form.photographerIds,
        ...(assignmentTeamChanged
          ? {
              reason:
                form.assignmentReason.trim() ||
                "Photographer assignment updated by admin",
            }
          : {}),
      });
    } else {
      succeeded = await create({
        studioId: form.studioId,
        eventTypeId: form.eventTypeId,
        clientEmail: form.clientEmail.trim(),
        ...(form.clientDisplayName.trim()
          ? { clientDisplayName: form.clientDisplayName.trim() }
          : {}),
        assignedPhotographerIds: form.photographerIds,
        ...detailsPayload(),
      });
    }
    setBusy(false);
    if (succeeded) {
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(EVENT_DRAFT_KEY);
      }
    }
  }

  const scheduleError =
    form.startsAt &&
    form.endsAt &&
    new Date(form.endsAt) <= new Date(form.startsAt)
      ? "The end time must be later than the start time."
      : "";
  const accessError =
    form.accessStartsAt &&
    form.accessExpiresAt &&
    new Date(form.accessExpiresAt) <= new Date(form.accessStartsAt)
      ? "QR expiry must be later than QR access start."
      : "";
  const selectedType = eventTypes.find((item) => item.id === form.eventTypeId);
  const selectedPlan = plans.find((item) => item.code === form.planCode);

  return (
    <>
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Event provisioning</p>
          <h1>Events & QR access</h1>
          <p>
            Configure the event, Client, approved partners, plan and QR policy
            before publication.
          </p>
        </div>
        <button
          className="button button-dark"
          onClick={startCreate}
          disabled={!eventTypes.some((item) => item.status === "active")}
        >
          <Plus size={16} /> New event
        </button>
      </div>
      {!eventTypes.some((item) => item.status === "active") && (
        <div className="admin-alert" role="status">
          <AlertTriangle size={16} /> Create or activate an event type before
          provisioning an event.
        </div>
      )}
      {open && (
        <form
          className="event-form professional-event-form admin-panel"
          onSubmit={submit}
        >
          <div className="panel-title">
            <div>
              <p className="eyebrow">{editing ? "Edit event" : "New event"}</p>
              <h2>
                {stage === "details"
                  ? "Event configuration"
                  : "Review before saving"}
              </h2>
              <p>
                {editing
                  ? "Existing memberships and QR identity remain intact."
                  : "Draft progress is stored safely in this browser."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close event form"
            >
              <X size={18} />
            </button>
          </div>
          <div className="event-form-progress" role="tablist">
            <span
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              className={stage === "details" ? "active" : "complete"}
              onClick={() => setStage("details")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ")
                  setStage("details");
              }}
            >
              <b>1</b> Details & policy
            </span>
            <span
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              className={stage === "review" ? "active" : ""}
              onClick={(event) => {
                const formEl = event.currentTarget.closest("form");
                if (formEl && !formEl.reportValidity()) return;
                setStage("review");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  const formEl = event.currentTarget.closest("form");
                  if (formEl && !formEl.reportValidity()) return;
                  setStage("review");
                }
              }}
            >
              <b>2</b> Review & confirm
            </span>
          </div>
          {stage === "details" ? (
            <div className="event-sections">
              <Section
                title="Event identity"
                text="Use the governed catalogue and a clear Client-facing title."
              >
                <div className="event-form-grid">
                  <label className="wide">
                    Event title
                    <input
                      required
                      minLength="2"
                      maxLength="200"
                      value={form.name}
                      onChange={(event) => setField("name", event.target.value)}
                    />
                  </label>
                  <label>
                    Event type
                    <select
                      required
                      value={form.eventTypeId}
                      onChange={(event) =>
                        setField("eventTypeId", event.target.value)
                      }
                    >
                      <option value="">Choose type</option>
                      {eventTypes
                        .filter(
                          (item) =>
                            item.status === "active" ||
                            item.id === editing?.eventType?.id,
                        )
                        .map((item) => (
                          <option value={item.id} key={item.id}>
                            {item.name}
                            {item.status !== "active" ? " (historical)" : ""}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Studio
                    <select
                      required
                      disabled={Boolean(editing)}
                      value={form.studioId}
                      onChange={(event) => chooseStudio(event.target.value)}
                    >
                      <option value="">Choose studio</option>
                      {studios.map((studio) => (
                        <option value={studio.id} key={studio.id}>
                          {studio.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </Section>
              <Section
                title="Client ownership"
                text="The Client receives event-scoped access through email OTP."
              >
                <div className="event-form-grid">
                  <label>
                    Client name
                    <input
                      minLength="2"
                      maxLength="120"
                      value={form.clientDisplayName}
                      onChange={(event) =>
                        setField("clientDisplayName", event.target.value)
                      }
                    />
                  </label>
                  <label className="wide">
                    Client email
                    <input
                      required
                      type="email"
                      maxLength="254"
                      value={form.clientEmail}
                      onChange={(event) =>
                        setField("clientEmail", event.target.value)
                      }
                    />
                    <small>An OTP invitation is prepared automatically.</small>
                  </label>
                </div>
              </Section>
              <Section
                title="Approved photographers"
                text="Admin controls every assignment. Paid events may be assigned directly from the approved directory or through an approved registration below."
              >
                <div className="photographer-picker">
                  {availablePhotographers.map((member) => (
                    <label key={member.id}>
                      <input
                        type="checkbox"
                        checked={form.photographerIds.includes(member.user.id)}
                        onChange={() => togglePhotographer(member.user.id)}
                      />
                      <span>
                        <strong>
                          {member.user?.displayName || member.user?.email}
                        </strong>
                        <small>{member.user?.email}</small>
                      </span>
                    </label>
                  ))}
                  {form.studioId && !availablePhotographers.length && (
                    <div className="event-inline-warning">
                      No approved Photographer is available. You can save this
                      event unassigned.
                    </div>
                  )}
                </div>
                {editing?.commercial && (
                  <div className="event-inline-warning">
                    Fixed paid plan: {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: editing.commercial.currency,
                    }).format(editing.commercial.totalAmountMinor / 100)} · Photographer 85%: {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: editing.commercial.currency,
                    }).format(editing.commercial.photographerShareMinor / 100)} · PhotoX 15%: {new Intl.NumberFormat("en-IN", {
                      style: "currency",
                      currency: editing.commercial.currency,
                    }).format(editing.commercial.platformCommissionMinor / 100)}
                  </div>
                )}
                {editing && applications.length > 0 && (
                  <div className="photographer-picker">
                    {applications.map((application) => (
                      <div key={application.id} className="event-inline-warning">
                        <strong>
                          {application.photographer?.displayName || application.photographer?.email}
                        </strong>
                        <small>Registration: {application.status}</small>
                        {application.status === "pending" && (
                          <span>
                            <button
                              type="button"
                              className="button button-dark"
                              disabled={busy}
                              onClick={() => reviewApplication(application, "approved")}
                            >
                              Approve & assign
                            </button>
                            <button
                              type="button"
                              className="button button-light"
                              disabled={busy}
                              onClick={() => reviewApplication(application, "rejected")}
                            >
                              Reject
                            </button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {editing && (
                  <label className="event-assignment-reason">
                    Assignment change reason
                    <textarea
                      maxLength="500"
                      minLength={assignmentTeamChanged ? 3 : undefined}
                      required={assignmentTeamChanged}
                      value={form.assignmentReason}
                      onChange={(event) =>
                        setField("assignmentReason", event.target.value)
                      }
                      placeholder="Required when adding or removing a photographer"
                    />
                  </label>
                )}
              </Section>
              <Section
                title="Schedule & venue"
                text="Dates are stored as exact instants with the selected display timezone."
              >
                <div className="event-form-grid">
                  <label>
                    Starts at
                    <input
                      required
                      type="datetime-local"
                      value={form.startsAt}
                      onChange={(event) =>
                        setField("startsAt", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Ends at
                    <input
                      type="datetime-local"
                      value={form.endsAt}
                      onChange={(event) =>
                        setField("endsAt", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Timezone
                    <select
                      value={form.timezone}
                      onChange={(event) =>
                        setField("timezone", event.target.value)
                      }
                    >
                      <option value="Asia/Kolkata">India Standard Time</option>
                      <option value="Asia/Dubai">Gulf Standard Time</option>
                      <option value="Europe/London">London</option>
                      <option value="America/New_York">New York</option>
                      <option value="America/Los_Angeles">Los Angeles</option>
                    </select>
                  </label>
                  <label>
                    Venue name
                    <input
                      maxLength="200"
                      value={form.venueName}
                      onChange={(event) =>
                        setField("venueName", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    City
                    <input
                      maxLength="120"
                      value={form.venueCity}
                      onChange={(event) =>
                        setField("venueCity", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Country
                    <input
                      maxLength="120"
                      value={form.venueCountry}
                      onChange={(event) =>
                        setField("venueCountry", event.target.value)
                      }
                    />
                  </label>
                  <label className="wide">
                    Address
                    <input
                      maxLength="500"
                      value={form.venueAddress}
                      onChange={(event) =>
                        setField("venueAddress", event.target.value)
                      }
                    />
                  </label>
                </div>
                {scheduleError && (
                  <div className="event-field-error">{scheduleError}</div>
                )}
              </Section>
              <Section
                title="Plan & retention"
                text="The Client activates this intended plan before Admin publication."
              >
                <div className="event-form-grid">
                  <label className="wide">
                    Subscription plan
                    <select
                      required
                      disabled={Boolean(editing && editing.status !== "draft")}
                      value={form.planCode}
                      onChange={(event) =>
                        setField("planCode", event.target.value)
                      }
                    >
                      <option value="">Choose plan</option>
                      {plans.map((plan) => (
                        <option value={plan.code} key={plan.code}>
                          {plan.name} · {formatBytes(plan.storageBytes)} ·{" "}
                          {plan.retentionExtensionDays} extension days
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Base retention days
                    <input
                      required
                      type="number"
                      min="1"
                      max="3650"
                      value={form.retentionDays}
                      onChange={(event) =>
                        setField("retentionDays", event.target.value)
                      }
                    />
                  </label>
                  {editing && (
                    <label className="policy-toggle wide">
                      <input
                        type="checkbox"
                        checked={form.billingRequired}
                        onChange={(event) =>
                          setField("billingRequired", event.target.checked)
                        }
                      />
                      <span>
                        <strong>Billing required</strong>
                        <small>
                          Uncheck to waive subscription billing and publish
                          without client payment.
                        </small>
                      </span>
                    </label>
                  )}
                </div>
              </Section>
              <Section
                title="QR & Guest policy"
                text="QR entry still requires email OTP and remains scoped to this event."
              >
                <div className="event-form-grid">
                  <label>
                    QR access starts
                    <input
                      type="datetime-local"
                      value={form.accessStartsAt}
                      onChange={(event) =>
                        setField("accessStartsAt", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    QR access expires
                    <input
                      type="datetime-local"
                      value={form.accessExpiresAt}
                      onChange={(event) =>
                        setField("accessExpiresAt", event.target.value)
                      }
                    />
                  </label>
                  <label className="policy-toggle wide">
                    <input
                      type="checkbox"
                      checked={form.guestUploadsEnabled}
                      onChange={(event) =>
                        setField("guestUploadsEnabled", event.target.checked)
                      }
                    />
                    <span>
                      <strong>Allow Guest uploads</strong>
                      <small>
                        Guest photos remain private until the uploader shares
                        them.
                      </small>
                    </span>
                  </label>
                </div>
                {accessError && (
                  <div className="event-field-error">{accessError}</div>
                )}
              </Section>
            </div>
          ) : (
            <div className="event-review">
              <article>
                <small>Event</small>
                <strong>{form.name}</strong>
                <span>{selectedType?.name}</span>
              </article>
              <article>
                <small>Client</small>
                <strong>{form.clientDisplayName || form.clientEmail}</strong>
                <span>{form.clientEmail}</span>
              </article>
              <article>
                <small>Schedule</small>
                <strong>{formatDate(form.startsAt)}</strong>
                <span>{form.timezone}</span>
              </article>
              <article>
                <small>Venue</small>
                <strong>{form.venueName || "Venue to be confirmed"}</strong>
                <span>
                  {[form.venueCity, form.venueCountry]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </article>
              <article>
                <small>Partner team</small>
                <strong>{form.photographerIds.length} Photographer(s)</strong>
                <span>Approved studio partners only</span>
              </article>
              <article>
                <small>Plan</small>
                <strong>{selectedPlan?.name || form.planCode}</strong>
                <span>{form.retentionDays} base retention days</span>
              </article>
              <article>
                <small>Guest policy</small>
                <strong>
                  {form.guestUploadsEnabled ? "Uploads allowed" : "View only"}
                </strong>
                <span>Email OTP remains mandatory</span>
              </article>
              <article>
                <small>Initial state</small>
                <strong>{editing ? editing.status : "Private draft"}</strong>
                <span>Publication remains a separate controlled action</span>
              </article>
            </div>
          )}
          <footer className="event-form-actions">
            {stage === "review" && (
              <button
                type="button"
                className="button button-light"
                onClick={() => setStage("details")}
              >
                <ArrowLeft size={17} />
                Edit details
              </button>
            )}
            {stage === "details" ? (
              <>
                <button
                  type="button"
                  className="button button-light"
                  disabled={busy || Boolean(scheduleError || accessError)}
                  onClick={(event) => {
                    const formEl = event.currentTarget.closest("form");
                    if (formEl && !formEl.reportValidity()) return;
                    setStage("review");
                  }}
                >
                  <Eye size={17} />
                  Review event
                </button>
                <button
                  type="submit"
                  disabled={busy || Boolean(scheduleError || accessError)}
                  className="button button-dark"
                >
                  {busy ? (
                    <LoaderCircle className="spin" size={17} />
                  ) : (
                    <ArrowRight size={17} />
                  )}
                  Next
                </button>
              </>
            ) : (
              <button
                type="submit"
                disabled={busy || Boolean(scheduleError || accessError)}
                className="button button-dark"
              >
                {busy ? (
                  <LoaderCircle className="spin" size={17} />
                ) : (
                  <Check size={17} />
                )}
                {editing ? "Confirm changes" : "Create private draft"}
              </button>
            )}
          </footer>
        </form>
      )}
      <section className="admin-panel event-table professional-event-table">
        <div className="event-list-toolbar">
          <div>
            <h2>Provisioned events</h2>
            <p>
              {events.length} provisioned event{events.length === 1 ? "" : "s"}
              {` · ${visibleRequests.length} open client request${visibleRequests.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <label>
            <Search size={15} />
            <input
              aria-label="Search events"
              placeholder="Search events or clients"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>
        <div className="event-status-tabs" aria-label="Filter events by status">
          {[
            "all",
            "requests",
            "draft",
            "scheduled",
            "live",
            "delivered",
            "expired",
            "archived",
          ].map((status) => (
            <button
              key={status}
              className={statusFilter === status ? "active" : ""}
              onClick={() => setStatusFilter(status)}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="table-head">
          <span>Event</span>
          <span>Client</span>
          <span>Photographer</span>
          <span>Plan</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {visibleRequests.map((item) => (
          <div className="table-row" key={`request-${item.id}`}>
            <span>
              <strong>
                {item.eventName || item.eventType?.name || "Event request"}
              </strong>
              <small>
                {item.reference} · {formatDate(item.startsAt)}
              </small>
            </span>
            <span>
              {item.clientName}
              <small>{item.email}</small>
            </span>
            <span>
              <small style={{ color: "var(--muted, #64748b)" }}>Pending conversion</small>
            </span>
            <span>
              <i className="status status-review">
                {item.quote?.planCode || "not quoted"}
              </i>
            </span>
            <span>
              <i className="status status-review">
                request: {item.status.replaceAll("_", " ")}
              </i>
            </span>
            <span className="event-actions professional-event-actions">
              <button
                className="button button-light"
                onClick={() => reviewRequests(item.id)}
              >
                <Inbox size={15} /> Review request
              </button>
            </span>
          </div>
        ))}
        {visibleEvents.length ? (
          visibleEvents.map((item) => {
            const paid = paidEventIds.has(item.id);
            const photographers = item.assignedPhotographers || [];
            const hasPhotographer = photographers.length > 0;
            return (
              <div className="table-row" key={item.id}>
                <span>
                  <strong>{item.name}</strong>
                  <small>
                    {item.eventType?.name || "Legacy event"} ·{" "}
                    {formatDate(item.startsAt)}
                  </small>
                </span>
                <span>
                  {item.client?.displayName || item.client?.email || "—"}
                </span>
                <span>
                  {hasPhotographer ? (
                    <div className="photographer-team-chips">
                      {photographers.map((p) => (
                        <span
                          key={p.id || p.email}
                          className="photographer-chip"
                          title={p.email || p.displayName}
                        >
                          📸 {p.displayName || p.email}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="status-warning" title="No photographer assigned to this event yet">
                      ⚠️ Not assigned
                    </span>
                  )}
                </span>
                <span>
                  <i
                    className={`status ${paid ? "status-paid" : "status-review"}`}
                  >
                    {item.billingRequired
                      ? paid
                        ? item.requestedPlanCode || "active"
                        : `${item.requestedPlanCode || "plan"} pending`
                      : item.requestedPlanCode || "not required"}
                  </i>
                </span>
                <span>
                  {item.status === "scheduled" && !hasPhotographer ? (
                    <i
                      className="status-warning"
                      title="Photographer not assigned yet. Assign a photographer before starting live mode."
                    >
                      awaiting photographer
                    </i>
                  ) : (
                    <i
                      className={`status ${["live", "scheduled", "delivered"].includes(item.status) ? "status-live" : "status-draft"}`}
                    >
                      {item.status}
                    </i>
                  )}
                </span>
                <span className="event-actions professional-event-actions">
                  <button
                    className="button button-light"
                    onClick={() => openGallery(item)}
                  >
                    <Images size={15} /> Gallery
                  </button>
                  {!["expired", "archived"].includes(item.status) && (
                    <button
                      className="button button-light"
                      onClick={() => startEdit(item)}
                    >
                      Edit
                    </button>
                  )}
                  {item.status === "draft" && item.billingRequired && !paid && (
                    <button
                      className="button button-light"
                      onClick={() => sponsorPlan(item)}
                      title="Activate this plan immediately as Admin without waiting for client payment"
                    >
                      <Sparkles size={14} /> Activate plan
                    </button>
                  )}
                  {item.status === "draft" && (
                    <button
                      disabled={item.billingRequired && !paid}
                      onClick={() => publish(item)}
                      className="button button-dark"
                      title={
                        item.billingRequired && !paid
                          ? `Plan activation pending (${(item.requestedPlanCode || "plan").toUpperCase()}). Click 'Activate plan' or wait for client payment to publish.`
                          : "Publish event"
                      }
                    >
                      <Send size={15} /> Publish
                    </button>
                  )}
                  {item.status === "scheduled" && (
                    <button
                      className="button button-dark"
                      disabled={!hasPhotographer}
                      style={!hasPhotographer ? { opacity: 0.55, cursor: "not-allowed" } : {}}
                      title={
                        !hasPhotographer
                          ? "Please assign a photographer before starting live mode."
                          : "Start live"
                      }
                      onClick={() => {
                        if (!hasPhotographer) {
                          setToast("Please assign a photographer to this event before starting live mode.");
                          return;
                        }
                        transition(item, "live");
                      }}
                    >
                      Start live
                    </button>
                  )}
                  {item.status === "live" && (
                    <button
                      className="button button-dark"
                      onClick={() => transition(item, "delivered")}
                    >
                      Mark delivered
                    </button>
                  )}
                  {item.status === "delivered" && (
                    <button
                      className="button button-dark"
                      onClick={() => transition(item, "expired")}
                    >
                      Mark expired
                    </button>
                  )}
                  {["scheduled", "live", "delivered"].includes(item.status) && (
                    <button
                      onClick={() => showQr(item)}
                      className="button button-light"
                    >
                      <QrCode size={15} /> QR
                    </button>
                  )}
                  {item.status !== "archived" && (
                    <button
                      className="catalogue-archive"
                      aria-label={`Archive ${item.name}`}
                      onClick={() => setArchiveTarget(item)}
                    >
                      <Archive size={15} />
                    </button>
                  )}
                  <button
                    className="button partner-danger"
                    aria-label={`Delete ${item.name}`}
                    title="Permanently delete event"
                    onClick={() => setDeleteTarget(item)}
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </span>
              </div>
            );
          })
        ) : visibleRequests.length ? null : (
          <div className="empty-row">No events match this view.</div>
        )}
      </section>
      {galleryTarget && (
        <div
          className="admin-modal event-gallery-layer"
          role="dialog"
          aria-modal="true"
        >
          <section className="event-gallery-modal">
            <header>
              <div>
                <p className="eyebrow">Event-scoped media</p>
                <h2>{galleryTarget.name}</h2>
                <p>
                  Admin view includes private Guest and Client uploads for this
                  event only.
                </p>
              </div>
              <div className="event-gallery-header-actions">
                <button
                  type="button"
                  className="button button-light"
                  disabled={galleryBusy}
                  onClick={() => openGallery(galleryTarget)}
                >
                  <RefreshCw className={galleryBusy ? "spin" : ""} size={15} />{" "}
                  Refresh
                </button>
                <button
                  type="button"
                  className="event-gallery-close"
                  aria-label="Close event gallery"
                  onClick={() => setGalleryTarget(null)}
                >
                  <X size={18} />
                </button>
              </div>
            </header>
            <BulkMediaUploader
              eventId={galleryTarget.id}
              api={adminMediaApi(token)}
              onUploaded={() => openGallery(galleryTarget)}
            />
            {galleryBusy ? (
              <div className="event-gallery-state">
                <LoaderCircle className="spin" size={24} /> Loading gallery…
              </div>
            ) : galleryError ? (
              <div className="event-gallery-state event-gallery-error">
                <AlertTriangle size={22} /> {galleryError}
              </div>
            ) : galleryPhotos.length ? (
              <>
                <div className="event-gallery-summary">
                  <article>
                    <small>Total images</small>
                    <strong>{galleryCounts.total}</strong>
                  </article>
                  <article>
                    <small>Ready</small>
                    <strong>{galleryCounts.ready}</strong>
                  </article>
                  <article>
                    <small>Shared</small>
                    <strong>{galleryCounts.shared}</strong>
                  </article>
                  <article>
                    <small>Private</small>
                    <strong>{galleryCounts.private}</strong>
                  </article>
                </div>
                <div className="event-gallery-toolbar">
                  <label className="event-gallery-search">
                    <Search size={15} />
                    <input
                      aria-label="Search event gallery"
                      placeholder="Search filename or uploader"
                      value={galleryQuery}
                      onChange={(event) => setGalleryQuery(event.target.value)}
                    />
                  </label>
                  <label>
                    Type
                    <select
                      value={galleryKind}
                      onChange={(event) => setGalleryKind(event.target.value)}
                    >
                      <option value="all">All uploads</option>
                      <option value="professional">Professional</option>
                      <option value="guest">Guest / Client</option>
                    </select>
                  </label>
                  <label>
                    Privacy
                    <select
                      value={galleryPrivacy}
                      onChange={(event) =>
                        setGalleryPrivacy(event.target.value)
                      }
                    >
                      <option value="all">All privacy</option>
                      <option value="shared">Shared</option>
                      <option value="private">Private</option>
                    </select>
                  </label>
                  <span>{visibleGalleryPhotos.length} shown</span>
                  <div className="event-gallery-bulk-actions">
                    <button
                      type="button"
                      className="button button-light"
                      disabled={
                        !visibleGalleryPhotos.length ||
                        Boolean(deletingPhotoIds.length)
                      }
                      onClick={toggleVisibleGalleryPhotoSelection}
                    >
                      {allVisibleGalleryPhotosSelected
                        ? "Clear visible"
                        : "Select visible"}
                    </button>
                    {selectedGalleryPhotos.length > 0 && (
                      <button
                        type="button"
                        className="button partner-danger"
                        disabled={Boolean(deletingPhotoIds.length)}
                        onClick={() =>
                          deleteGalleryPhotos(selectedGalleryPhotos)
                        }
                      >
                        {deletingPhotoIds.length ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <Trash2 size={15} />
                        )}
                        Delete {selectedGalleryPhotos.length} selected
                      </button>
                    )}
                  </div>
                </div>
                <div
                  className={`event-gallery-content ${inspectedPhoto ? "has-inspector" : ""}`}
                >
                  {visibleGalleryPhotos.length ? (
                    <div className="event-gallery-grid">
                      {visibleGalleryPhotos.map((photo) => (
                        <article
                          className={[
                            inspectedPhoto?.id === photo.id ? "selected" : "",
                            selectedPhotoIds.has(photo.id) ? "marked" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          key={photo.id}
                        >
                          <label className="event-gallery-select">
                            <input
                              type="checkbox"
                              checked={selectedPhotoIds.has(photo.id)}
                              disabled={Boolean(deletingPhotoIds.length)}
                              onChange={() =>
                                toggleGalleryPhotoSelection(photo.id)
                              }
                              aria-label={`Select ${photo.originalFilename || "image"}`}
                            />
                          </label>
                          <button
                            type="button"
                            className="event-gallery-card-button"
                            onClick={() => setInspectedPhoto(photo)}
                          >
                            <div className="event-gallery-media">
                              {photo.mediaUrl ? (
                                <img
                                  src={photo.mediaUrl}
                                  alt={
                                    photo.caption ||
                                    photo.originalFilename ||
                                    "Event upload"
                                  }
                                />
                              ) : (
                                <GalleryMediaPlaceholder
                                  status={photo.status}
                                  failure={photo.failure}
                                />
                              )}
                              <span
                                className={`event-gallery-privacy ${photo.privacy}`}
                              >
                                {photo.privacy === "private" && (
                                  <LockKeyhole size={11} />
                                )}
                                {photo.privacy}
                              </span>
                            </div>
                            <div className="event-gallery-meta">
                              <strong>
                                {photo.originalFilename || "Event photo"}
                              </strong>
                              <small>
                                {photo.uploader?.displayName || "Event member"}{" "}
                                · {photo.kind}
                              </small>
                            </div>
                          </button>
                          <button
                            type="button"
                            className="event-gallery-mobile-delete"
                            disabled={Boolean(deletingPhotoIds.length)}
                            onClick={() => deleteGalleryPhotos([photo])}
                            aria-label={`Delete ${photo.originalFilename || "image"}`}
                            title="Delete image"
                          >
                            {deletingPhotoIds.includes(photo.id) ? (
                              <LoaderCircle className="spin" size={16} />
                            ) : (
                              <X size={17} />
                            )}
                          </button>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="event-gallery-state event-gallery-filter-empty">
                      <Search size={24} /> No images match these filters.
                    </div>
                  )}
                  {inspectedPhoto && (
                    <aside className="event-photo-inspector">
                      <button
                        type="button"
                        className="event-gallery-close"
                        aria-label="Close image details"
                        onClick={() => setInspectedPhoto(null)}
                      >
                        <X size={16} />
                      </button>
                      <div className="event-photo-inspector-media">
                        {inspectedPhoto.mediaUrl ? (
                          <img
                            src={inspectedPhoto.mediaUrl}
                            alt={
                              inspectedPhoto.caption ||
                              inspectedPhoto.originalFilename ||
                              "Selected event upload"
                            }
                          />
                        ) : (
                          <GalleryMediaPlaceholder
                            status={inspectedPhoto.status}
                            failure={inspectedPhoto.failure}
                          />
                        )}
                      </div>
                      <p className="eyebrow">Image details</p>
                      <h3>
                        {inspectedPhoto.originalFilename || "Event photo"}
                      </h3>
                      {inspectedPhoto.caption && (
                        <p>{inspectedPhoto.caption}</p>
                      )}
                      <dl>
                        <div>
                          <dt>Uploader</dt>
                          <dd>
                            {inspectedPhoto.uploader?.displayName ||
                              "Event member"}
                          </dd>
                        </div>
                        <div>
                          <dt>Type</dt>
                          <dd>{inspectedPhoto.kind}</dd>
                        </div>
                        <div>
                          <dt>Privacy</dt>
                          <dd>{inspectedPhoto.privacy}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd>{inspectedPhoto.status}</dd>
                        </div>
                        <div>
                          <dt>Size</dt>
                          <dd>{formatFileSize(inspectedPhoto.sizeBytes)}</dd>
                        </div>
                        <div>
                          <dt>Uploaded</dt>
                          <dd>{formatDate(inspectedPhoto.createdAt)}</dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="button button-dark"
                        disabled={
                          originalBusy || inspectedPhoto.status !== "ready"
                        }
                        onClick={() => openOriginal(inspectedPhoto)}
                      >
                        {originalBusy ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <ExternalLink size={15} />
                        )}
                        Open original
                      </button>
                      <button
                        type="button"
                        className="button partner-danger"
                        disabled={Boolean(deletingPhotoIds.length)}
                        onClick={() => deleteGalleryPhotos([inspectedPhoto])}
                      >
                        {deletingPhotoIds.includes(inspectedPhoto.id) ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <Trash2 size={15} />
                        )}
                        {deletingPhotoIds.includes(inspectedPhoto.id)
                          ? "Deleting…"
                          : "Delete image"}
                      </button>
                    </aside>
                  )}
                </div>
              </>
            ) : (
              <div className="event-gallery-state">
                <Images size={28} /> No images have been uploaded to this event.
              </div>
            )}
          </section>
        </div>
      )}
      {archiveTarget && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <section className="catalogue-confirm">
            <span>
              <Archive size={22} />
            </span>
            <h2>Archive {archiveTarget.name}?</h2>
            <p>
              The event becomes read-only and its historical relationships
              remain available.
            </p>
            <div>
              <button
                className="button button-light"
                onClick={() => setArchiveTarget(null)}
              >
                Keep event
              </button>
              <button
                className="button partner-danger"
                onClick={async () => {
                  if (await transition(archiveTarget, "archived")) {
                    setArchiveTarget(null);
                  }
                }}
              >
                Archive event
              </button>
            </div>
          </section>
        </div>
      )}
      {deleteTarget && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <section className="catalogue-confirm">
            <span>
              <Trash2 size={22} />
            </span>
            <h2>Delete {deleteTarget.name}?</h2>
            <p>
              This permanently removes the event, gallery media, memberships
              and related test records. Its name and details can then be used
              again. This cannot be undone.
            </p>
            <div>
              <button
                className="button button-light"
                onClick={() => setDeleteTarget(null)}
              >
                Keep event
              </button>
              <button
                className="button partner-danger"
                onClick={async () => {
                  if (await remove(deleteTarget)) setDeleteTarget(null);
                }}
              >
                Delete permanently
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
